'use client';

import Image from 'next/image';
import playGlyph from '@images/10years/stories/ico-play-circle.svg';
import { useState, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { TrackedButton } from '@/components/button/tracked-button';
import type { StoryData } from '@/services/ten-years-stories';
import {
  INLINE_TAGS,
  POSTER_SIZES,
  TEN_YEARS_POSITION,
  storyItemKey,
} from '../../_const/stories';
// 임시 모달(첫 로드 번들)이 첫 전환의 새 화면이라, 이름 규칙은 모달 청크가 아니라 여기서 불러야 첫 열기에도 걸린다
import './story-transition.css';

/** 미디어 자리. ty-story-media 는 View Transition 이름 — 스냅샷이 패널 잘림 밖에 그려져 위 모서리를 직접 둥글린다 */
export default function StoryMedia({ children }: { children: ReactNode }) {
  return (
    <div className="ty-story-media relative aspect-video w-full overflow-hidden bg-3b-background-medium lg:rounded-t-[40px]">
      {children}
    </div>
  );
}

/** 두 겹: 밑깔개(이미 받은 이미지, 주소 그대로) + srcset 포스터. 둘 다 eager — lazy 면 스냅샷 때 요청도 안 한다 */
export function StoryMediaPoster({
  src,
  backdrop,
}: {
  src: string;
  backdrop?: string;
}) {
  return (
    <>
      {backdrop && (
        <Image
          src={backdrop}
          alt=""
          fill
          unoptimized
          loading="eager"
          className="object-cover"
        />
      )}
      <Image
        src={src}
        alt=""
        fill
        sizes={POSTER_SIZES}
        loading="eager"
        className="object-cover"
      />
    </>
  );
}

/** 포스터와 재생 버튼 — 누르면 영상으로 바뀐다. 스토리가 바뀌면 부르는 쪽이 key 로 재생 상태를 초기화한다 */
export function StoryMediaPlayer({
  content,
  backdrop,
}: {
  content: StoryData;
  backdrop?: string;
}) {
  const t = useTranslations('TenYears.stories');
  const [playing, setPlaying] = useState(false);

  // YouTube 임베드는 로드만으로 수백 KB 를 끌고 오므로 ▶ 를 누른 뒤에만 넣는다
  if (content.type === 'video' && playing) {
    // 퍼가기 주소에 붙어 오는 ?si= 등은 그대로 두고 재생 옵션만 더한다
    const src = new URL(content.embedUrl);
    src.searchParams.set('autoplay', '1');
    src.searchParams.set('rel', '0');
    return (
      <iframe
        src={src.toString()}
        title={t('modal.videoTitle', {
          title: t.markup(
            `items.${storyItemKey(content.day)}.title`,
            INLINE_TAGS,
          ),
        })}
        allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sandbox="allow-scripts allow-same-origin allow-presentation"
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 size-full"
      />
    );
  }

  return (
    <>
      <StoryMediaPoster src={content.poster} backdrop={backdrop} />
      {content.type === 'video' && (
        <TrackedButton
          type="button"
          label={`10years-stories-play-day-${content.day}`}
          position={TEN_YEARS_POSITION}
          aria-label={t('modal.play')}
          onClick={() => setPlaying(true)}
          className="absolute left-1/2 top-1/2 size-20 -translate-x-1/2 -translate-y-1/2 text-white lg:size-[100px]"
        >
          {/* Figma ▶ 글리프(사이트에 같은 아이콘이 없다)를 마스크로 — 색은 버튼의 currentColor */}
          <span
            aria-hidden
            className="block size-full bg-current"
            style={{ mask: `url(${playGlyph.src}) center / contain no-repeat` }}
          />
        </TrackedButton>
      )}
    </>
  );
}
